/**
 * Interface for Web Content SDK message
 */
interface IWebContentMessage {
  /**
   * request id used by sdk to track callback function.
   */
  id?: number;
  /**
   * sdk function arguments for corresponding handler.
   */
  args?: (object | string | boolean)[];
  /**
   * timestamp of when the message was created by App SDK.
   */
  timestamp?: number;
}

/**
 * Interface for OS date & time format settings
 */
export interface ILocaleInfo {
  platform: PlatformType;
  regionalFormat: string;
  shortDate: string;
  longDate: string;
  shortTime: string;
  longTime: string;
}

export enum HubNames {
  office = 'Office',
  orange = 'Orange',
  outlook = 'Outlook',
  teams = 'Teams',
}

export enum PlatformType {
  windows = 'windows',
  macOs = 'macos',
}

/**
 * Interface for Sdk request message
 */
export interface IWebContentRequestMessage extends IWebContentMessage {
  /**
   * sdk function name to get corresponding handler.
   */
  func: string;
}

/**
 * Interface for Sdk response message
 */
export interface IWebContentResponseMessage extends IWebContentMessage {
  /**
   * If the message is partial, it indicates future responses for the given message ID are still coming.
   */
  isPartialResponse?: boolean;

  /**
   * target origin.
   */
  origin?: string;
}

/**
 * Interface for Sdk message Event
 */
export interface IWebContentMessageEvent {
  /**
   * Message Request
   */
  data: IWebContentRequestMessage;
  /**
   * request event origin.
   */
  origin?: string;
  /**
   * send async response to app
   */
  sendAsyncResponse?: (sdkRequestMessage: IWebContentRequestMessage) => void;
}

export enum Themes {
  default = 'default',
  dark = 'dark',
  highContrast = 'contrast',
}

/**
 * User setting changes that can be subscribed to,
 */
export enum UserSettingTypes {
  /**
   * Use this key to subscribe to changes in user's file open preference
   */
  fileOpenPreference = 'fileOpenPreference',
  /**
   * Use this key to subscribe to theme changes
   */
  theme = 'theme',
}

/**
 * Interface for Setting message data
 */
export interface ISettings {
  /**
   * A suggested display name for the new content.
   * In the settings for an existing instance being updated, this call has no effect.
   */
  suggestedDisplayName?: string;
  /**
   * Sets the URL to use for the content of this instance.
   */
  contentUrl: string;
  /**
   * Sets the URL for the removal configuration experience.
   */
  removeUrl?: string;
  /**
   * Sets the URL to use for the external link to view the underlying resource in a browser.
   */
  websiteUrl?: string;
  /**
   * The developer-defined unique ID for the entity to which this content points.
   */
  entityId: string;
}

/**
 * Interface for Frame
 */
export interface IFrameInfo extends HTMLElement {
  /**
   * web frame type .
   */
  src: string;
  /**
   * frame contentWindow.
   */
  contentWindow?: Window | null;
  /**
   * postMessage to send message to frame
   * @param message
   */
  postMessage?: (message: string) => void;
}

/**
 * Interface for Message Handler response
 */
export interface IWebContentMessageHandlerResult {
  /**
   * target element
   */
  target: HTMLIFrameElement;
  /**
   * id use for callback
   */
  id: number;
  /**
   * function arguments
   */
  args: object[];
}

/**
 * Enum for host client types
 */
export enum HostClientType {
  web = 'web',
  desktop = 'desktop',
}

/**
 * Enum for frame contexts to determine scope of different sdk apis
 */
export enum FrameContexts {
  settings = 'settings',
  content = 'content',
  authentication = 'authentication',
  remove = 'remove',
  task = 'task',
  sidePanel = 'sidePanel',
}

/**
 * MessageHandler type
 */
export type MessageHandler = {
  /**
   * value is as defined in SdkMessageTypes
   */
  messageName: string;
  handler: (...args: unknown[]) => unknown;
};
